import { UserJourney } from "../UserJourney";
import Survey from "../Survey";
import Answer from "../Survey/Answer";
import { HealthRiskAssessmentConfiguration, HealthStationConfiguration } from "./Configuration";
import BaseConfiguration from "./Configuration/BaseConfiguration";
import Consent from "../Consent/Consent";
import moment from "moment";
import DiagnosticsData from "../diagnosticsData/diagnosticsData";
import { QuestionCodes, Answers } from "../UserJourney/UserJourneyMap";
import DateConverter from "../Converters/DateConverter";
import flatMap from "lodash/flatMap";
import SurveyResults from "./SurveyResults";

export const APP_FLOWS = {
    SCRIPTS_NOW: "SCRIPTS_NOW"
};

export default class Response {
    constructor(type = Response.TYPE_STANDARD) {
        this._survey = null;

        /** @type {../UserJourney} */
        this._userJourney = null;
        this._type = type;
        this._userUUID = null;
        this._previousAnswers = {};
        this._healthCheckExtra = null;
        this._finished = false;
        this._sequence = 1;
        this._responseId = null;
        this._recommendations = {};
        this._isTest = false;
        this._configuration = null;
        this._trackedConsents = [];
        this._events = [];
        this._createdAt = null;
        this._order = null;
        this._isDod = false;
        this._dodMedicationOrders = null;
        this._dodPharmacyRecord = null;
        this._dodDefaultPharmacyRecord = null;
        this._diagnosticsData = null;
        this._appVersion = null;
        this._printOnly = false;
        this._scriptsNowOrder = null;
        this._appFlow = null;
        this._surveyResults = new SurveyResults();
        this._language = null;
    }

    static get Events() {
        return {
            AnsweredQuestion: "AnsweredQuestion",
            StartedHealthCheck: "StartedHealthCheck",
            FinishedHealthCheck: "FinishedHealthCheck",
            SkippedLogin: "SkippedLogin",
            StartedDodOrder: "StartedDodOrder",
            FinishedDodOrder: "FinishedDodOrder",
            SurveyStarted: "SurveyStarted",
            SurveyFinished: "SurveyFinished",
            DodSearchMedication: "DodSearchMedication",
            DodMedicationSelectionChanged: "DodMedicationSelectionChanged",
            DodOrderSummary: "DodOrderSummary",
            DodOrderQuantityChanged: "DodOrderQuantityChanged",
            DodPharmacyLocation: "DodPharmacyLocation",
            DodSearchPharmacy: "DodSearchPharmacy",
            DodPharmacyChanged: "DodPharmacyChanged",
            DodPharmacyLocationNext: "DodPharmacyLocationNext",
            ScriptsNowStart: "ScriptsNowStart",
            ScriptsNowOnBoarding: "ScriptsNowOnBoarding",
            ScriptsNowCancelOnboarding: "ScriptsNowCancelOnboarding",
            ScriptsNowContinueOnboarding: "ScriptsNowContinueOnboarding",
            ScriptsNowSearchMedication: "ScriptsNowSearchMedication",
            ScriptsNowSearchMedicationTriggered: "ScriptsNowSearchMedicationTriggered",
            ScriptsNowMedicationSelectionChanged: "ScriptsNowMedicationSelectionChanged",
            ScriptsNowOrderSummary: "ScriptsNowOrderSummary",
            ScriptsNowOrderQuantityChanged: "ScriptsNowOrderQuantityChanged",
            ScriptsNowPharmacyLocation: "ScriptsNowPharmacyLocation",
            ScriptsNowPharmacyLocationNext: "ScriptsNowPharmacyLocationNext",
            ScriptsNowFinishJourney: "ScriptsNowFinishJourney",
            ScriptsNowPaymentSummaryPlaceOrder: "ScriptsNowPaymentSummaryPlaceOrder",
            ScriptsNowPaymentSummaryCancelOrder: "ScriptsNowPaymentSummaryCancelOrder",
            ScriptsNowPaymentStateChanged: "ScriptsNowPaymentStateChanged",
            ScriptsNowPaymentButtonClicked: "ScriptsNowPaymentButtonClicked"
        };
    }

    static get TYPE_STANDARD() {
        return "standard";
    }

    static get TYPE_HEALTH_CHECK() {
        return "healthCheck";
    }

    static get TYPE_HEALTH_RISK_ASSESSMENT() {
        return "healthRiskAssessment";
    }

    /**
     * A factory helper for preparing the response for HealthCheck contexts
     *
     * @returns {Response}
     */
    static createForHealthCheck(configuration = {}) {
        let response = new Response(this.TYPE_HEALTH_CHECK);
        response.configuration = new HealthStationConfiguration(configuration);
        return response;
    }

    /**
     * A factory helper for preparing the response for Health Risk Assessment contexts
     *
     * @returns {Response}
     */
    static createForHealthRiskAssessment(configuration) {
        let response = new Response(this.TYPE_HEALTH_RISK_ASSESSMENT);
        response.configuration = new HealthRiskAssessmentConfiguration(configuration);
        return response;
    }

    /**
     * Set an object that uses question codes as the keys with an array of answers
     * to use as a basis for pre-populating questions.
     *
     * Converts the plain-text answers into Answer objects to match the way that Questions manage/add answers
     *
     * @param {Object} answers
     */
    setPreviousAnswers(answers) {
        let previousAnswers = {};

        for (let questionCode in answers) {
            let questionAnswers = answers[questionCode];
            if (questionAnswers.length) {
                previousAnswers[questionCode] = [];
                for (let answer of questionAnswers) {
                    previousAnswers[questionCode].push(new Answer(answer));
                }
            }
        }

        this._previousAnswers = previousAnswers;
    }

    /**
     *
     * @param id
     * @return {Response}
     */
    set userUUID(id) {
        this._userUUID = id;
        return this;
    }

    /**
     *
     * @return {string|null}
     */
    get userUUID() {
        return this._userUUID;
    }

    /**
     * @returns {string|null}
     */
    get userEmail() {
        if (
            this._healthCheckExtra &&
            this._healthCheckExtra.hasOwnProperty("user") &&
            this._healthCheckExtra.user &&
            this._healthCheckExtra.user.hasOwnProperty("emailAddress")
        ) {
            return this._healthCheckExtra.user.emailAddress;
        } else {
            return null;
        }
    }

    /**
     * @param {string} language
     * @return {Response}
     *
     */
    set language(language) {
        this._language = language;
        return this;
    }

    /**
     * @return {string|null}
     */
    get language() {
        return this._language;
    }

    /**
     *
     * @return {boolean}
     */
    isTypeStandard() {
        return this._type === Response.TYPE_STANDARD;
    }

    /**
     *
     * @return {boolean}
     */
    isTypeHealthCheck() {
        return this._type === Response.TYPE_HEALTH_CHECK;
    }

    /**
     *
     * @return {boolean}
     */
    get isPrintOnlyHealthCheck() {
        return this.isTypeHealthCheck() && this.printOnly;
    }

    /**
     *
     * @return {boolean}
     */
    isTypeHealthRiskAssessment() {
        return this._type === Response.TYPE_HEALTH_RISK_ASSESSMENT;
    }

    /**
     *
     * @param {UserJourney} userJourney
     * @returns {Response}
     */
    setUserJourney(userJourney) {
        this._userJourney = userJourney;
        this._userJourney.setResponse(this);
        return this;
    }

    /**
     *
     * @returns {UserJourney}
     */
    getUserJourney() {
        return this._userJourney;
    }

    /**
     * Set Survey
     *
     * @param survey
     * @return {Response}
     */
    setSurvey(survey) {
        this._survey = survey;
        this._survey.setResponse(this);
        return this;
    }

    /**
     *
     * @return {Survey}
     */
    getSurvey() {
        return this._survey;
    }

    /**
     * Returns an object of answers with question codes as the Object key.
     * Answers to Questions without codes are not returned
     *
     * @returns {Object}
     */
    getAnswersForPreFill() {
        let answers = {};
        // Codes whose current value came from a real answer rather than a provisional prefill.
        // Several question codes appear on more than one page (the ethnicity carrier and basic
        // question are each shared by multiple surveys), and merging pages in order would
        // otherwise let a later page's stale prefill replace the answer the user actually gave.
        let realAnswerCodes = new Set();

        if (this._userJourney) {
            let userJourneyPages = this._userJourney.getUserJourneyPages();

            for (let ujp = 0; ujp < userJourneyPages.length; ujp++) {
                let userJourneyPage = userJourneyPages[ujp];

                switch (true) {
                    case userJourneyPage.getPageType().isTypeSurvey():
                        this._mergeAnswersFromSurvey(answers, realAnswerCodes, userJourneyPage.getSurvey());
                        break;

                    /* istanbul ignore next */ case userJourneyPage.getPageType().isTypeConsent():
                        if (userJourneyPage.getConsent().getSurvey()) {
                            this._mergeAnswersFromSurvey(
                                answers,
                                realAnswerCodes,
                                userJourneyPage.getConsent().getSurvey()
                            );
                        }
                        break;
                }
            }

            // FIXME PLZ
            // When recompositing on the API side, the consent resource is different whereas on the frontend it's the same resource.
            // When we move answers to the Response object (SU-578), the problem should go away.
            /* istanbul ignore next */
            for (let trackedConsent of this.getTrackedConsents()) {
                if (trackedConsent.getSurvey()) {
                    this._mergeAnswersFromSurvey(answers, realAnswerCodes, trackedConsent.getSurvey());
                }
            }
        } else if (this._survey) {
            answers = this._extractAnswersFromSurveyForQuestionsWithACode(this._survey);
        }

        // use any previous answers first. Survey Question answers are considered more up to date therefore will override
        // any defined previous answer
        answers = { ...this._previousAnswers, ...answers };

        // calculate and append/overwrite any derived answers
        answers = { ...answers, ...this.generateDerivedAnswers(answers) };

        return answers;
    }

    /**
     * Returns the set of question codes collected by the (non-consent) surveys in this Response's
     * User Journey. These are the codes another survey is responsible for, and which a later consent
     * survey expects to prefill from.
     *
     * @returns {Set<String>}
     * @private
     */
    _getQuestionCodesFromJourneySurveys() {
        const codes = new Set();

        if (!this._userJourney) {
            return codes;
        }

        for (const userJourneyPage of this._userJourney.getUserJourneyPages()) {
            if (!userJourneyPage.getPageType().isTypeSurvey() || !userJourneyPage.getSurvey()) {
                continue;
            }

            for (const group of userJourneyPage.getSurvey().getQuestionGroups()) {
                for (const question of group.getQuestions()) {
                    if (question.getCode()) {
                        codes.add(question.getCode());
                    }
                }
            }
        }

        return codes;
    }

    /**
     * A consent survey re-lists questions (e.g. BP readings) that should be prefilled from earlier
     * surveys. Returns true when the given code is collected by another survey in the journey but has
     * no answer to prefill from (e.g. the source survey was skipped) — such questions must NOT be asked
     * again in the consent. Codes that no other survey collects (e.g. LAST_NAME) return false, so they
     * are still asked.
     *
     * @param {String} questionCode
     * @returns {Boolean}
     */
    isUnfilledPrefillTarget(questionCode) {
        if (!questionCode) {
            return false;
        }

        const prefillAnswers = this.getAnswersForPreFill()[questionCode];
        if (Array.isArray(prefillAnswers) && prefillAnswers.length > 0) {
            return false;
        }

        return this._getQuestionCodesFromJourneySurveys().has(questionCode);
    }

    /**
     * Generates derived answers for a specific set of aggregate question codes (eg. age)
     * @return {Object.<String, [Answer]>}
     */
    generateDerivedAnswers(answers) {
        const derivedAnswers = {};

        // generate Age
        const dateOfBirthAnswer = answers[QuestionCodes.dateOfBirth];
        if (dateOfBirthAnswer && dateOfBirthAnswer.length > 0) {
            const fromDate = moment(dateOfBirthAnswer[0].answer, "YYYY-MM-DD", true);
            const age = DateConverter.calculateYearsDifference(fromDate, moment());

            /* istanbul ignore else */
            if (!isNaN(age)) {
                derivedAnswers[QuestionCodes.age] = [new Answer(age)];
            }
        }

        // Re-populate IS_SMOKER from whichever smoking question the survey asked.
        // Priority: SMOKING_DO_YOU_SMOKE -> IS_SMOKER (already normalised) ->
        // DO_YOU_SMOKE_EXCL_VAPING. Note the two questions use different answer
        // enums: SMOKING_DO_YOU_SMOKE offers no "some days" option, so occasional
        // smoking can only arrive via DO_YOU_SMOKE_EXCL_VAPING.
        //
        // Any current smoking counts as a smoker here, so "on some days" derives to
        // Yes and therefore raises the smoking referral trigger (see the rule sets
        // keyed on IS_SMOKER = "Yes").
        //
        // This intentionally DIVERGES from AusDiabetesRisk.isSmokerScore, which reads
        // the same DO_YOU_SMOKE_EXCL_VAPING answer but scores "on some days" as 0.
        // AUSDRISK is a published instrument asking specifically about daily smoking,
        // so its scoring must not be changed to match this. Do not unify the two.
        //
        // heartAge/Survey/calculate.js resolves the same three codes in the opposite
        // order (isSmoker ?? doYouSmokeExcludingVaping ?? smokingDoYouSmoke). The two
        // cannot currently disagree because the SMOKING survey carries only
        // SMOKING_DO_YOU_SMOKE while IS_SMOKER and DO_YOU_SMOKE_EXCL_VAPING appear in
        // the ausDiabetesRisk/heartAge/myHealth surveys, so the code sets are disjoint.
        // If a journey ever pairs the SMOKING survey with HeartAge, align the two.
        const smokingDoYouSmoke = answers[QuestionCodes.smokingDoYouSmoke]?.[0]?.answer;
        const legacyIsSmoker = answers[QuestionCodes.isSmoker]?.[0]?.answer;
        const doYouSmokeExcludingVaping = answers[QuestionCodes.doYouSmokeExcludingVaping]?.[0]?.answer;

        if (smokingDoYouSmoke) {
            derivedAnswers[QuestionCodes.isSmoker] = [
                new Answer(smokingDoYouSmoke == Answers.SmokingDoYouSmoke.yes ? Answers.YesNo.yes : Answers.YesNo.no)
            ];
        } else if (legacyIsSmoker) {
            derivedAnswers[QuestionCodes.isSmoker] = [new Answer(legacyIsSmoker)];
        } else if (doYouSmokeExcludingVaping) {
            derivedAnswers[QuestionCodes.isSmoker] = [
                new Answer(
                    doYouSmokeExcludingVaping == Answers.SmokingDoYouSmokeNew.yes ||
                    doYouSmokeExcludingVaping == Answers.SmokingDoYouSmokeNew.someDays
                        ? Answers.YesNo.yes
                        : Answers.YesNo.no
                )
            ];
        }

        return derivedAnswers;
    }

    /**
     * Returns if any Survey associated to this Response contains Questions that contain PII and have been answered
     *
     * @returns {boolean}
     */
    hasAnswersContainingPII() {
        let hasAnswersContainingPII = false;
        let consentSurvey;

        if (this._userJourney) {
            for (let userJourneyPage of this._userJourney.getUserJourneyPages()) {
                switch (true) {
                    case userJourneyPage.getPageType().isTypeSurvey(): // FIXME: suppress coverage
                        /* istanbul ignore else */ if (!hasAnswersContainingPII && userJourneyPage.getSurvey()) {
                            hasAnswersContainingPII = this._surveyHasAnswersContainingPII(userJourneyPage.getSurvey());
                        }
                        break;

                    /* istanbul ignore next */ case userJourneyPage.getPageType().isTypeConsent():
                        consentSurvey = userJourneyPage.getConsent().getSurvey();
                        if (!hasAnswersContainingPII && consentSurvey) {
                            hasAnswersContainingPII = this._surveyHasAnswersContainingPII(consentSurvey);
                        }
                        break;
                }
            }
        } else {
            hasAnswersContainingPII = this._surveyHasAnswersContainingPII(this._survey);
        }

        return hasAnswersContainingPII;
    }

    /**
     *
     * @param {Survey} survey
     * @private
     */
    _surveyHasAnswersContainingPII(survey) {
        let hasAnswersContainingPII = false;

        for (let questionGroup of survey.getQuestionGroups()) {
            for (let question of questionGroup.getQuestions()) {
                if (!hasAnswersContainingPII && question.getAnswersContainPII() && question.getAnswers().length > 0) {
                    hasAnswersContainingPII = true;
                }
            }
        }

        return hasAnswersContainingPII;
    }

    /**
     * Returns an object of answers for the given Survey where the answered question must have a question code
     *
     * @param {Survey} survey
     * @private
     */
    /**
     * Merge one survey's answers into the journey-wide map, keeping real answers ahead of
     * provisional prefills for the same question code. Everything else keeps the previous
     * last-page-wins behaviour.
     *
     * @param {Object} answers - journey-wide map being built (mutated)
     * @param {Set<string>} realAnswerCodes - codes currently held by a real answer (mutated)
     * @param {Survey} survey
     */
    _mergeAnswersFromSurvey(answers, realAnswerCodes, survey) {
        let seenInThisSurvey = new Set();

        for (let group of survey.getQuestionGroups()) {
            for (let question of group.getQuestions()) {
                let code = question.getCode();
                if (!code) continue;

                let isRealAnswer = question.isStateAnswered();
                if (!isRealAnswer && !question.isStateAnsweredByPreFill()) continue;

                // Within a survey the first question with a code wins, as before.
                if (seenInThisSurvey.has(code)) continue;
                seenInThisSurvey.add(code);

                // A prefill must never displace a real answer contributed by another survey.
                if (!isRealAnswer && realAnswerCodes.has(code)) continue;

                answers[code] = question.getAnswers();

                if (isRealAnswer) {
                    realAnswerCodes.add(code);
                } else {
                    realAnswerCodes.delete(code);
                }
            }
        }
    }

    _extractAnswersFromSurveyForQuestionsWithACode(survey) {
        let answers = {};
        for (let group of survey.getQuestionGroups()) {
            for (let question of group.getQuestions()) {
                if (
                    question.getCode() &&
                    (question.isStateAnswered() || question.isStateAnsweredByPreFill()) &&
                    !answers.hasOwnProperty(question.getCode())
                ) {
                    answers[question.getCode()] = question.getAnswers();
                }
            }
        }

        return answers;
    }

    /**
     * Convert this Response instance into a POJO
     */
    toJSON() {
        let trackedConsents = this._trackedConsents.map(consent => consent.toJSON());

        return {
            finished: this.finished,
            sequence: this.sequence,
            responseId: this._responseId,
            userUUID: this._userUUID,
            type: this._type,
            survey: this._survey ? this._survey.toJSON() : null,
            userJourney: this._userJourney ? this._userJourney.toJSON() : null,
            healthCheckExtra: this._healthCheckExtra,
            recommendations: this._recommendations,
            isTest: this.isTest,
            configuration: this._configuration ? this._configuration.raw : {},
            trackedConsents,
            events: this._events,
            createdAt: this._createdAt,
            appVersion: this._appVersion,
            order: this._order,
            isDod: this._isDod,
            diagnosticsData: this._diagnosticsData ? this._diagnosticsData.toJson() : null,
            printOnly: this.printOnly,
            scriptsNowOrder: this._scriptsNowOrder,
            appFlow: this._appFlow,
            surveyResults: this._surveyResults.results,
            language: this._language
        };
    }

    /**
     * Designed for use server side where a persisted response JSON is used to recreate the Response object state prior to submission.
     * Hydration should not trigger any Response, User Journey or Survey model internal state adaptations.
     *
     * @param data
     * @return {Response}
     */
    fromJSON(data) {
        this._responseId = data.responseId;
        this._type = data.type;
        this._userUUID = data.userUUID;
        this._finished = data.finished;
        this._sequence = data.sequence;
        this._recommendations = data.recommendations ? data.recommendations : {};
        this.isTest = data.isTest;
        this._healthCheckExtra = data.healthCheckExtra;
        this._events = data.events ? data.events : [];
        this._createdAt = data.createdAt;
        this._appVersion = data.appVersion;
        this._order = data.hasOwnProperty("order") ? data.order : [];
        this._isDod = data.hasOwnProperty("isDod") ? data.isDod : false;
        this._printOnly = data.printOnly || false;
        this._language = data.language || "en-GB";

        // replace with configuration objects
        if (this.isTypeHealthCheck()) {
            this.configuration = new HealthStationConfiguration(data.configuration);
        } else if (this.isTypeHealthRiskAssessment()) {
            this.configuration = new HealthRiskAssessmentConfiguration(data.configuration);
        }

        this._trackedConsents = [];
        if (data.hasOwnProperty("trackedConsents")) {
            this._trackedConsents = data.trackedConsents.map(consent => {
                let c = new Consent(consent.schema);
                c.fromJSON(consent, this);
                return c;
            });
        }

        if (data.userJourney) {
            let userJourney = new UserJourney(data.userJourney.schema);
            this.setUserJourney(userJourney);

            userJourney.getUserJourneyPages().map((userJourneyPage, userJourneyPageIndex) => {
                userJourneyPage.setResolved(data.userJourney.userJourneyPages[userJourneyPageIndex].resolved);
                userJourneyPage.setPreConditionEvaluationResult(
                    data.userJourney.userJourneyPages[userJourneyPageIndex].preConditionEvaluationResult
                );

                let survey = userJourneyPage.getSurvey();
                if (survey) {
                    let json = data.userJourney.userJourneyPages[userJourneyPageIndex].survey;

                    this.hydrateSurvey(survey, json);
                }
            });
        } else if (data.survey) {
            let survey = new Survey(data.survey.schema);
            this.setSurvey(survey);

            this.hydrateSurvey(survey, data.survey);
        }

        this._diagnosticsData = data.diagnosticsData == null ? null : new DiagnosticsData(data.diagnosticsData);
        this._scriptsNowOrder = data.scriptsNowOrder;
        this._appFlow = data.appFlow;
        return this;
    }

    /**
     * Hydrate a survey with the given json
     *
     * @param survey
     * @param json
     */
    hydrateSurvey(survey, json) {
        survey.getQuestionGroups().map((questionGroup, questionGroupIndex) => {
            questionGroup.getQuestions().map((question, questionIndex) => {
                let questionJSON = json.questionGroups[questionGroupIndex].questions[questionIndex];
                question.fromJSON(questionJSON);
            });
        });

        survey.setState(json.state);
        survey.setCompleteState(json.completeState);
    }

    /**
     * Return answers for the question with the given code
     *
     * @param code
     * @return {Array}
     */
    getAnswersForQuestionByCode(code) {
        let answers = null;
        let answersForQuestionsWithCodes = this.getAnswersForPreFill();

        if (answersForQuestionsWithCodes.hasOwnProperty(code)) {
            answers = answersForQuestionsWithCodes[code];
        }
        return answers;
    }

    /**
     * Finalize-time backfill: for each allowlisted question code that has a value in the citizen
     * prefill map, fill every journey question with that code that has no answer yet, marking it
     * answerPreFilled so it lands in the final response.
     *
     * Runs AFTER navigation, so it is immune to the mid-journey skip (canAskNextQuestion ->
     * setStateDidNotQualify) that drops already-resolved ethnicity out of the response for
     * returning users. Two guards keep it safe:
     *   - scoped to `codes` (never a blanket fill), and
     *   - canBeAsked() must pass, so a question whose precondition genuinely failed (a real
     *     didNotQualify, not a skip-because-resolved) is never fabricated.
     * Existing answers are never overwritten.
     *
     * @param {Object} answersByCode - citizen previousAnswersForHealthCheck (code -> [value] | value)
     * @param {string[]} codes - allowlist of question codes to backfill
     * @returns {Response}
     */
    backfillPrefillAnswers(answersByCode = {}, codes = []) {
        if (this._userJourney == null || answersByCode == null) return this;

        for (let code of codes) {
            let raw = answersByCode[code];
            if (raw == null) continue;
            let value = Array.isArray(raw) ? raw[0] : raw;
            if (value == null) continue;

            // If this code was answered anywhere in the journey, that answer is this session's
            // truth and no stale citizen value may be planted for it. The per-question guard
            // below is not enough: the same code appears on several pages (the ethnicity carrier
            // and basic question are each shared by multiple surveys), and getAnswersForPreFill
            // merges pages with Object.assign — so backfilling a later page would silently
            // override the answer the user just gave and persist the old value back to citizen
            // data.
            if (this._hasAnswerForQuestionCode(code)) continue;

            for (let page of this._userJourney.getUserJourneyPages()) {
                let survey = page.getSurvey();
                if (survey == null) continue;

                for (let group of survey.getQuestionGroups()) {
                    let question = group.getQuestionByQuestionCode(code);
                    if (question == null) continue;
                    if (question.getAnswers().length > 0) continue; // never overwrite a real answer
                    if (!question.canBeAsked()) continue; // never fabricate a precondition-failed answer

                    question.setPrefilledAnswer(value);
                }
            }
        }

        return this;
    }

    /**
     * Does any question with this code, anywhere in the journey, already hold an answer?
     *
     * @param {string} code
     * @returns {boolean}
     */
    _hasAnswerForQuestionCode(code) {
        for (let page of this._userJourney.getUserJourneyPages()) {
            let survey = page.getSurvey();
            if (survey == null) continue;

            for (let group of survey.getQuestionGroups()) {
                let question = group.getQuestionByQuestionCode(code);
                if (question != null && question.getAnswers().length > 0) return true;
            }
        }

        return false;
    }

    /**
     * Returns all answers with the question Id as the key
     *
     * @return {Object}
     */
    getAnswers() {
        let answers = {};

        // not doing this ignore means that the else block is expected.
        /* istanbul ignore else */
        if (this._userJourney) {
            for (let userJourneyPage of this._userJourney.getUserJourneyPages()) {
                let survey = userJourneyPage.getSurvey();
                if (survey) {
                    answers = Object.assign({}, answers, this._getAnswersFromSurvey(survey));
                }
            }

            // FIXME PLZ
            // When recompositing on the API side, the consent resource is different whereas on the frontend it's the same resource.
            // When we move answers to the Response object (SU-578), the problem should go away.
            /* istanbul ignore next */
            for (let trackedConsent of this.getTrackedConsents()) {
                if (trackedConsent.getSurvey()) {
                    answers = Object.assign(answers, this._getAnswersFromSurvey(trackedConsent.getSurvey()));
                }
            }
        } else if (this._survey) {
            answers = this._getAnswersFromSurvey(this._survey);
        }

        return answers;
    }

    /**
     * Returns object with questionId as the key with the value being another object containing the question code and answers
     *
     * @param survey
     * @private
     */
    _getAnswersFromSurvey(survey) {
        let questionAnswers = {};
        /** @type {../Survey/QuestionGroup[]} */
        for (let group of survey.getQuestionGroups()) {
            /** @type {../Survey/Question[]} */
            let questions = group.getQuestions();
            for (let question of questions) {
                if (question.hasBeenResolved()) {
                    questionAnswers[question.getQuestionId()] = {
                        questionCode: question.getCode(),
                        displayText: question.getDisplayText(),
                        answers: question.getAnswers()
                    };
                }
            }
        }

        return questionAnswers;
    }

    getAnsweredQuestions() {
        let answeredQuestions = [];
        let surveyQuestions = [];
        let consentQuestions = [];
        if (this._userJourney) {
            let pages = this._userJourney.getUserJourneyPages();
            if (pages && pages.length > 0) {
                pages = pages.filter(page => page.getSurvey() != null);
                surveyQuestions = flatMap(pages, page => page.getSurvey().getAnsweredQuestion());
            }

            // this is just bad
            let trackedConsents = this.getTrackedConsents();
            if (trackedConsents && trackedConsents.length > 0) {
                trackedConsents = trackedConsents.filter(tc => tc.getSurvey() != null);
                consentQuestions = flatMap(trackedConsents, tc => tc.getSurvey().getAnsweredQuestion());
            }

            answeredQuestions = [...surveyQuestions, ...consentQuestions];
        } else if (this._survey) {
            answeredQuestions = this._survey.getAnsweredQuestion();
        }

        return answeredQuestions;
    }

    hasEvent(eventType) {
        const result = this.events.find(event => event.type === eventType) || null;
        return !!result;
    }

    /**
     * Gets health-check extra data
     * @return {Object|null}
     */
    getHealthCheckExtra() {
        return this._healthCheckExtra;
    }

    /**
     * Sets the health-check extra data.
     * @param healthCheckExtra
     */
    setHealthCheckExtra(healthCheckExtra) {
        this._healthCheckExtra = healthCheckExtra;
        return this;
    }

    get finished() {
        return this._finished;
    }

    setIsFinished() {
        return (this._finished = true);
    }

    incrementSequence() {
        this._sequence++;
    }

    get sequence() {
        return this._sequence;
    }

    get responseId() {
        return this._responseId;
    }

    set responseId(id) {
        this._responseId = id;
    }

    get recommendations() {
        return this._recommendations;
    }

    /**
     * Stores a recommendation, replacing any existing recommendation for that ad slot.
     *
     * Replacing by ad slot is the right default: a result page that re-fetches its slot after
     * an answer changes must supersede the stale recommendation rather than keep both.
     *
     * One ad slot legitimately holds SEVERAL recommendations at once, though: the lifestyle
     * survey's `LIFESTYLE_AREA_RECOMMENDATION` serves up to seven areas from a single slot,
     * distinguished only by `criteria.area`. Keying those on the ad slot alone would keep just
     * the last one written. So when — and only when — the criteria name an area, the area
     * joins the key. No other risk sets `criteria.area`, so every existing caller keeps exactly
     * the replace-by-slot behaviour it had.
     *
     * The key is a map key for iteration only; `ProcessResponseTask` dedupes on `campaignAdId`.
     *
     * @param recommendation
     */
    storeRecommendation(recommendation) {
        const area = recommendation.criteria ? recommendation.criteria.area : null;
        const key = area ? `${recommendation.adSlotId}:${area}` : recommendation.adSlotId;

        this._recommendations[key] = recommendation;
    }

    set isTest(value) {
        this._isTest = value;
    }

    get isTest() {
        return this._isTest;
    }

    get campaignId() {
        return this._configuration.campaignId;
    }

    /**
     *
     * @param {BaseConfiguration} config
     */
    set configuration(config) {
        if (!(config instanceof BaseConfiguration)) {
            throw new Error("Configuration must be an instance of BaseConfiguration");
        }

        this._configuration = config;
    }

    /**
     *
     * @returns {BaseConfiguration|null}
     */
    get configuration() {
        return this._configuration;
    }

    /**
     *
     * @returns {[{Object}]}
     */
    getTrackedConsents() {
        return this._trackedConsents;
    }

    trackConsent(consent) {
        let isTracked = false;
        for (let existingConsent of this._trackedConsents) {
            if (existingConsent.consentId === consent.consentId) {
                isTracked = true;
            }
        }

        if (!isTracked) {
            this._trackedConsents.push(consent);
        }
    }

    /**
     * Adds an event of a certain type with optional data
     *
     * @param {string} type
     * @param {object} data
     */
    addEvent(type, data = {}) {
        this._events.push({
            timestamp: moment.utc().toISOString(),
            type,
            data
        });
    }

    /**
     *
     * @returns {Array}
     */
    get events() {
        return this._events;
    }

    /**
     *
     * @returns {Array}
     */
    get surveyResults() {
        return this._surveyResults;
    }

    /**
     * @returns {string}
     */
    get createdAt() {
        return this._createdAt;
    }

    /**
     * Gets the app version
     * @returns {string}
     */
    get appVersion() {
        return this._appVersion;
    }

    /**
     * Set the user's order (things they want to purchase on DOD journey) not what position they are in
     *
     * @param order
     */
    set order(order) {
        this._order = order;
    }

    /**
     * Gets the user's order (things they want to purchase on DOD journey) not what position they are in
     * @returns {null}
     */
    get order() {
        return this._order;
    }

    /**
     * Sets whether this response is associated with a DOD journey
     * @param value
     */
    set isDod(value) {
        this._isDod = value;
    }

    /**
     * Gets whether this response is associated with a DOD journey
     * @returns {boolean}
     */
    get isDod() {
        return this._isDod;
    }

    set printOnly(value) {
        this._printOnly = value;
    }

    get printOnly() {
        return this._printOnly;
    }

    /**
     * Data containers, not hydrated to the final response
     */

    set dodMedicationOrders(order) {
        this._dodMedicationOrders = order;
    }

    get dodMedicationOrders() {
        return this._dodMedicationOrders;
    }

    set dodPharmacyRecord(record) {
        this._dodPharmacyRecord = record;
    }

    get dodPharmacyRecord() {
        return this._dodPharmacyRecord;
    }

    set dodDefaultPharmacyRecord(record) {
        this._dodDefaultPharmacyRecord = record;
    }

    get dodDefaultPharmacyRecord() {
        return this._dodDefaultPharmacyRecord;
    }

    set diagnosticsData(value) {
        this._diagnosticsData = value;
    }

    get diagnosticsData() {
        return this._diagnosticsData;
    }

    set scriptsNowOrder(value) {
        this._scriptsNowOrder = value;
    }

    get scriptsNowOrder() {
        return this._scriptsNowOrder;
    }

    set appFlow(value) {
        this._appFlow = value;
    }

    get appFlow() {
        return this._appFlow;
    }

    isAppFlowScriptsNow() {
        return this._appFlow === APP_FLOWS.SCRIPTS_NOW;
    }
}
